import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { addDays, formatCents, formatDateTime, mountainDay } from "@/lib/format";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { PaidWith } from "./PaymentBits";
import { ResolveButton } from "./PaymentFixDialogs";
import { countRunPayments, runResult, TRIGGER, type BillingRun } from "./runs";
import { useContactNames } from "./useContactNames";
import type { PaymentRow } from "./orders";

/** Billing → Billing runs: the 7 AM, 1 PM and 7 PM (Mountain Time) runs, what each charged, and the payments to look at.
 *  Friday go-live step 22's check. Looking changes nothing. */
export function BillingRunsPage() {
  const runs = useQuery({
    queryKey: ["billing-runs"],
    queryFn: () => api<{ runs_enabled: boolean; runs: BillingRun[] }>("/api/v1/billing/runs" + qs({ limit: 30 })),
    refetchInterval: 60_000,
  });
  const oldest = runs.data?.runs.at(-1)?.run_date?.slice(0, 10);
  const since = oldest ? (oldest < addDays(mountainDay(), -7) ? oldest : addDays(mountainDay(), -7)) : undefined;
  const payments = useQuery({
    queryKey: ["billing-run-payments", since],
    queryFn: () => api<{ payments: PaymentRow[] }>("/api/v1/payments" + qs({ since, limit: 500 })).then((r) => r.payments),
    enabled: !!since,
    refetchInterval: 60_000,
  });
  const pays = payments.data ?? [];
  const counts = countRunPayments(runs.data?.runs ?? [], pays);
  const weekAgo = addDays(mountainDay(), -7);
  const failed = pays.filter((p) => p.status === "failed" && p.attempted_at.slice(0, 10) >= weekAgo);
  // Payments without a clear answer, however old (a charge still going through shows here for a few seconds too).
  const unsureQ = useQuery({
    queryKey: ["billing-run-payments", "unsure"],
    queryFn: async () => {
      const [u, p] = await Promise.all(
        ["unknown", "pending"].map((status) => api<{ payments: PaymentRow[] }>("/api/v1/payments" + qs({ status, limit: 100 })).then((r) => r.payments)),
      );
      return [...u, ...p].sort((a, b) => b.attempted_at.localeCompare(a.attempted_at));
    },
    refetchInterval: 60_000,
  });
  const unsure = unsureQ.data ?? [];
  const name = useContactNames([...failed, ...unsure].map((p) => p.contact_id));
  const on = runs.data?.runs_enabled;
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader
        title="Billing runs"
        sub="Renewal and installment charges, three times a day (7 AM, 1 PM, 7 PM Mountain Time)"
        actions={
          runs.data && (
            <>
              <Badge variant={on ? "success" : "muted"}>{on ? "Runs are on" : "Runs are off"}</Badge>
              <Link to="/settings" search={{ tab: "settings" }} className="text-sm text-[var(--color-primary)] hover:underline">
                Change in Settings
              </Link>
            </>
          )
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Each run makes the invoices that are due and queues their charges; the charges go through within a few minutes, so the counts below fill in shortly after a run.
          Keap still charges the memberships it bills. Looking at this page changes nothing. Times are Mountain Time.
        </p>
      </PageHeader>

      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        {runs.isLoading && <Skeleton className="m-3 h-32" />}
        {runs.error && <p className="p-3 text-sm text-[var(--color-danger-fg)]">Couldn't load the billing runs: {runs.error.message}</p>}
        {payments.error && <p className="px-3 pb-2 text-sm text-[var(--color-danger-fg)]">Couldn't load the payments, so the charged and declined counts are missing: {payments.error.message}</p>}
        {runs.data && runs.data.runs.length === 0 && <Empty title="No billing runs yet.">The first one starts at the next 7 AM, 1 PM or 7 PM after runs are switched on.</Empty>}
        {runs.data && runs.data.runs.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Started (MT)</TH>
                <TH>Result</TH>
                <TH className="text-right">New invoices</TH>
                <TH className="text-right">Charges queued</TH>
                <TH className="text-right">Charged</TH>
                <TH className="text-right">Declined</TH>
                <TH className="text-right">Checking</TH>
              </tr>
            </THead>
            <tbody>
              {runs.data.runs.map((r) => {
                const res = runResult(r);
                const c = counts.get(r.id);
                const loaded = !!payments.data;
                return (
                  <TR key={r.id}>
                    <TD className="num whitespace-nowrap">
                      {formatDateTime(r.started_at)}
                      <div className="text-xs text-[var(--color-muted-foreground)]">{TRIGGER[r.trigger] ?? r.trigger}</div>
                    </TD>
                    <TD>
                      <Badge variant={res.tone}>{res.label}</Badge>
                      {r.error && <div className="max-w-[16rem] text-xs text-[var(--color-danger-fg)]">{r.error}</div>}
                    </TD>
                    <TD className="num text-right">{r.status === "skipped" ? "" : r.invoices_created}</TD>
                    <TD className="num text-right">{r.status === "skipped" ? "" : r.charges_queued}</TD>
                    <TD className="num text-right">{loaded && c ? (c.charged ? `${c.charged} (${formatCents(c.chargedCents)})` : "0") : ""}</TD>
                    <TD className={`num text-right ${c && c.failed ? "font-semibold text-[var(--color-danger-fg)]" : ""}`}>{loaded && c ? (c.failed ? `${c.failed} (${formatCents(c.failedCents)})` : "0") : ""}</TD>
                    <TD className="num text-right">{loaded && c ? c.processing || "" : ""}</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <PaymentList
        title="Declined in the last 7 days"
        empty="No declined payments in the last 7 days."
        rows={failed}
        name={name}
        loading={payments.isLoading && !!since}
        error={payments.error?.message}
        hint="The member gets the payment-failed email (when billing emails are live) and the next runs retry. Open the order to see what's owed."
      />
      <PaymentList
        title="Payments to check"
        empty="Nothing to check: every payment has a clear answer."
        rows={unsure}
        name={name}
        loading={unsureQ.isLoading}
        error={unsureQ.error?.message}
        hint="The gateway's answer was lost. The CRM checks again by itself; if it can't, look the payment up in the gateway and record what it shows."
        resolve
      />
    </div>
  );
}

function PaymentList({
  title,
  empty,
  rows,
  name,
  loading,
  hint,
  resolve,
  error,
}: {
  title: string;
  empty: string;
  rows: PaymentRow[];
  name: (id: number) => string;
  loading: boolean;
  hint: string;
  resolve?: boolean;
  error?: string;
}) {
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {rows.length > 0 && <Badge variant={resolve ? "warning" : "destructive"}>{rows.length}</Badge>}
      </CardHeader>
      <p className="px-4 pb-2 text-xs text-[var(--color-muted-foreground)]">{hint}</p>
      {loading && <Skeleton className="m-3 h-16" />}
      {error && <p className="px-4 pb-4 text-sm text-[var(--color-danger-fg)]">Couldn't load these payments: {error}</p>}
      {!loading && !error && rows.length === 0 && <p className="px-4 pb-4 text-sm text-[var(--color-muted-foreground)]">{empty}</p>}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>When (MT)</TH>
              <TH>Person</TH>
              <TH className="text-right">Amount</TH>
              <TH>Why</TH>
              <TH>Order</TH>
              {resolve && <TH className="w-px" />}
            </tr>
          </THead>
          <tbody>
            {rows.map((p) => (
              <TR key={p.id}>
                <TD className="num whitespace-nowrap text-xs">
                  {formatDateTime(p.attempted_at)}
                  <div className="text-[var(--color-muted-foreground)]">
                    <PaidWith p={p} />
                  </div>
                </TD>
                <TD className="max-w-[12rem] truncate">
                  <Link to="/contacts/$contactId" params={{ contactId: String(p.contact_id) }} className="text-[var(--color-primary)] hover:underline">
                    {name(p.contact_id)}
                  </Link>
                </TD>
                <TD className="num text-right">{formatCents(p.amount_cents)}</TD>
                <TD className="max-w-[18rem] text-xs text-[var(--color-muted-foreground)]">{p.failure_message || (p.source === "retry" ? "retry of an earlier decline" : "")}</TD>
                <TD>
                  {p.order_id ? (
                    <Link to="/billing/orders/$orderId" params={{ orderId: String(p.order_id) }} className="num text-[var(--color-primary)] hover:underline">
                      #{p.order_id}
                    </Link>
                  ) : (
                    <span className="text-xs text-[var(--color-muted-foreground)]">none</span>
                  )}
                </TD>
                {resolve && (
                  <TD>
                    <ResolveButton item={{ kind: "payment", id: p.id, amount_cents: p.amount_cents, gateway: p.gateway }} />
                  </TD>
                )}
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
